import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Shared palette (matches the site: navy + orange, Apple-like neutral surfaces).
const ORANGE = '#EA6F2E';
const NAVY = '#1A3A5C';
const BORDER = '#E3E6EC';
const MUTED = '#6B7280';

// Reusable control styles for a consistent, professional toolbar + forms.
const btnBase = {
  padding: '8px 14px', fontSize: '12.5px', fontWeight: 600, borderRadius: '8px',
  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px',
  lineHeight: 1, whiteSpace: 'nowrap', transition: 'all .15s',
};
const btnPrimary = { ...btnBase, background: ORANGE, color: '#fff', border: `1px solid ${ORANGE}` };
const btnGhost   = { ...btnBase, background: '#fff', color: NAVY, border: `1px solid ${BORDER}` };
const btnDanger  = { ...btnBase, background: '#fff', color: '#B42318', border: '1px solid #F1C7C2' };
const fieldStyle = {
  width: '100%', padding: '9px 11px', fontSize: '13px', border: `1px solid ${BORDER}`,
  borderRadius: '8px', outline: 'none', boxSizing: 'border-box', color: '#1F2937', background: '#fff',
};
const fieldLabel = {
  display: 'block', fontSize: '10.5px', fontWeight: 700, marginBottom: '5px',
  textTransform: 'uppercase', letterSpacing: '0.04em', color: MUTED,
};
const iconBtn = {
  background: 'none', border: 'none', cursor: 'pointer', padding: '5px', borderRadius: '6px',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};

const InventoryManager = () => {
  const [inventory, setInventory] = useState([]);
  const [stats, setStats] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStaff, setFilterStaff] = useState('all');
  const [activeTab, setActiveTab] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    item_type: '',
    brand: '',
    model: '',
    status: '',
    condition: '',
    staff_id: '',
    office_id: '',
    serial_number: '',
    asset_tag: '',
    mac_address: ''
  });
  const [staffList, setStaffList] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [importCategory, setImportCategory] = useState('furniture');
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedReplacement, setSelectedReplacement] = useState('');
  
  const emptyForm = {
    category: '',
    item_type: '',
    brand: '',
    model: '',
    serial_number: '',
    asset_tag: '',
    hostname: '',
    mac_address: '',
    description: '',
    condition: '',
    notes: '',
    quantity: 1,
    status: '',
    staff_id: ''
  };
  const [editForm, setEditForm] = useState(emptyForm);

  const savedUser = localStorage.getItem('user');
  const user = savedUser && savedUser !== 'undefined' ? JSON.parse(savedUser) : null;
  // Full inventory console (see-all + edit/delete) = admins OR the designated
  // inventory managers by username (mirrors backend INVENTORY_VIEW_ALL).
  const INVENTORY_MANAGERS = ['memmetti', 'jlamirault', 'sfoley', 'ptaylor'];
  const isAdmin = user?.role === 'admin' || INVENTORY_MANAGERS.includes(user?.username);

  useEffect(() => {
    fetchInventory();
    if (isAdmin) {
      fetchStats();
      fetchStaffList();
    }
  }, []);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const fetchInventory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventory(response.data);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/export/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchStaffList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaffList(response.data);
    } catch (error) {
      console.error('Error fetching staff list:', error);
    }
  };

  const fetchAvailableItems = async (category) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const available = response.data.filter(item => 
        !item.staff_id && 
        item.category === category &&
        (item.quantity || 0) > 0
      );
      setAvailableItems(available);
    } catch (error) {
      console.error('Error fetching available items:', error);
    }
  };

  const handleAssignClick = (item) => {
    setSelectedItem(item);
    setSelectedStaff('');
    setShowAssignModal(true);
  };

  const handleEditClick = (item) => {
    setIsAdding(false);
    setSelectedItem(item);
    setEditForm({
      category: item.category || '',
      item_type: item.item_type || '',
      brand: item.brand || '',
      model: item.model || '',
      serial_number: item.serial_number || '',
      asset_tag: item.asset_tag || '',
      hostname: item.hostname || '',
      mac_address: item.mac_address || '',
      description: item.description || '',
      condition: item.condition || '',
      notes: item.notes || '',
      quantity: item.quantity != null ? item.quantity : 1,
      status: item.status || '',
      staff_id: item.staff_id != null ? String(item.staff_id) : ''
    });
    setShowEditModal(true);
  };

  const handleAddClick = () => {
    setIsAdding(true);
    setSelectedItem(null);
    setEditForm({ ...emptyForm });
    setShowEditModal(true);
  };

  const handleDelete = async (item) => {
    const label = [item.brand, item.model, item.item_type].filter(Boolean).join(' ') || item.description || 'this item';
    if (!window.confirm(`Delete "${label}" from inventory? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_URL}/inventory/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Item deleted');
      fetchInventory();
      fetchStats();
    } catch (error) {
      showToast('Delete failed: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const handleReplaceClick = async () => {
    await fetchAvailableItems(selectedItem.category);
    setShowReplaceModal(true);
  };

  const handleReplace = async () => {
    if (!selectedReplacement) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Unassign current item
      await axios.put(`${process.env.REACT_APP_API_URL}/inventory/${selectedItem.id}`, {
        category: selectedItem.category,
        item_type: selectedItem.item_type,
        brand: selectedItem.brand,
        model: selectedItem.model,
        serial_number: selectedItem.serial_number,
        description: selectedItem.description,
        condition: selectedItem.condition,
        notes: selectedItem.notes,
        staff_id: null,
        status: 'available',
        quantity: selectedItem.quantity
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Assign new item
      await axios.post(`${process.env.REACT_APP_API_URL}/inventory/assign`, {
        itemId: parseInt(selectedReplacement),
        staffId: selectedItem.staff_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showToast('Item replaced successfully');
      setShowReplaceModal(false);
      setShowEditModal(false);
      setSelectedItem(null);
      setSelectedReplacement('');
      fetchInventory();
      fetchStats();
    } catch (error) {
      showToast('Replacement failed: ' + (error.response?.data?.error || error.message), 'error');
    }
    setSaving(false);
  };


  const handleAssign = async () => {
    if (!selectedStaff || selectedStaff === 'new') return;
    
    setAssigning(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${process.env.REACT_APP_API_URL}/inventory/assign`, {
        itemId: selectedItem.id,
        staffId: parseInt(selectedStaff)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showToast('Item assigned successfully');
      setShowAssignModal(false);
      setSelectedItem(null);
      setSelectedStaff('');
      fetchInventory();
      fetchStats();
    } catch (error) {
      showToast('Assignment failed: ' + (error.response?.data?.error || error.message), 'error');
    }
    setAssigning(false);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const staffIdVal = editForm.staff_id === '' ? null : parseInt(editForm.staff_id);
      const qtyVal = editForm.quantity === '' || editForm.quantity == null ? 1 : parseInt(editForm.quantity);
      // If no status chosen, derive a sensible one from assignment.
      const statusVal = editForm.status || (staffIdVal ? 'active' : 'available');

      if (isAdding) {
        await axios.post(`${process.env.REACT_APP_API_URL}/inventory`, {
          category: editForm.category,
          item_type: editForm.item_type,
          brand: editForm.brand,
          model: editForm.model,
          serial_number: editForm.serial_number,
          asset_tag: editForm.asset_tag,
          hostname: editForm.hostname,
          mac_address: editForm.mac_address,
          description: editForm.description,
          condition: editForm.condition,
          notes: editForm.notes,
          quantity: qtyVal,
          staff_id: staffIdVal,
          created_by: user?.id || null
        }, { headers: { Authorization: `Bearer ${token}` } });
        showToast('Item added successfully');
      } else {
        await axios.put(`${process.env.REACT_APP_API_URL}/inventory/${selectedItem.id}`, {
          ...editForm,
          staff_id: staffIdVal,
          quantity: qtyVal,
          status: statusVal
        }, { headers: { Authorization: `Bearer ${token}` } });
        showToast('Item updated successfully');
      }

      setShowEditModal(false);
      setSelectedItem(null);
      setIsAdding(false);
      fetchInventory();
      fetchStats();
    } catch (error) {
      showToast((isAdding ? 'Add failed: ' : 'Update failed: ') + (error.response?.data?.error || error.message), 'error');
    }
    setSaving(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportFile(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
      const preview = lines.slice(1, 6).map(line => {
        const values = line.split(',').map(v => v.trim().replace(/['"]/g, ''));
        const row = {};
        headers.forEach((h, i) => row[h] = values[i] || '');
        return row;
      });
      setImportPreview(preview);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
      const rows = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim().replace(/['"]/g, ''));
        const row = {};
        headers.forEach((h, i) => row[h] = values[i] || '');
        return row;
      });
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${process.env.REACT_APP_API_URL}/inventory/import`, {
          category: importCategory,
          items: rows
        }, { headers: { Authorization: `Bearer ${token}` }});
        showToast(`Successfully imported ${rows.length} items`);
        setShowImport(false);
        setImportFile(null);
        setImportPreview([]);
        fetchInventory();
      } catch (error) {
        showToast('Import failed: ' + (error.response?.data?.error || error.message), 'error');
      }
      setImporting(false);
    };
    reader.readAsText(importFile);
  };

  const downloadTemplate = (category) => {
    let headers = '';
    if (category === 'furniture') headers = 'item_type,description,quantity,condition,brand,model';
    if (category === 'equipment') headers = 'computer_type,manufacturer,model,serial_number,asset_tag,hostname,mac_address';
    if (category === 'software') headers = 'software_name,version,license_key,seats';
    const blob = new Blob([headers + '\n'], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${category}_template.csv`;
    a.click();
  };

  // Distinct non-empty values for a given field across the loaded inventory, sorted
  const distinctValues = (field) => {
    const set = new Set();
    inventory.forEach(item => {
      const v = item[field];
      if (v !== null && v !== undefined && String(v).trim() !== '') set.add(String(v));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  };

  // Distinct offices (by office_id) present in the inventory
  const distinctOffices = () => {
    const map = new Map();
    inventory.forEach(item => {
      if (item.office_id !== null && item.office_id !== undefined && String(item.office_id).trim() !== '') {
        const id = String(item.office_id);
        if (!map.has(id)) {
          map.set(id, item.office_name || item.office_number || `Office ${id}`);
        }
      }
    });
    return Array.from(map.entries())
      .sort((a, b) => a[1].localeCompare(b[1], undefined, { sensitivity: 'base' }));
  };

  const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  const clearFilters = () => setFilters({
    category: '',
    item_type: '',
    brand: '',
    model: '',
    status: '',
    condition: '',
    staff_id: '',
    office_id: '',
    serial_number: '',
    asset_tag: '',
    mac_address: ''
  });

  // The exact names a row's assignee can be matched by (first, last, "first last").
  const assigneeNames = (item) => [
    item.first_name,
    item.last_name,
    `${item.first_name || ''} ${item.last_name || ''}`.trim(),
  ].filter(Boolean).map(s => s.toLowerCase());

  // If what the user typed EXACTLY equals someone's name, narrow to that person
  // only — so "Jon" finds Jon Lamirault's assets, not also Kaitlyn Jones. Any other
  // query falls back to broad partial search (so "Pat" still finds Patricia, "Lenovo"
  // finds all Lenovos, serials/asset tags match, etc.).
  const searchQuery = searchTerm.trim().toLowerCase();
  const queryIsExactName =
    searchQuery !== '' && inventory.some(item => assigneeNames(item).includes(searchQuery));

  // Typeahead: distinct assignee full names that match what's typed (max 8).
  const nameSuggestions = (() => {
    if (searchQuery === '') return [];
    const names = new Set();
    inventory.forEach(item => {
      const full = `${item.first_name || ''} ${item.last_name || ''}`.trim();
      if (full) names.add(full);
    });
    return Array.from(names)
      .filter(n => n.toLowerCase().includes(searchQuery) && n.toLowerCase() !== searchQuery)
      .sort((a, b) => a.localeCompare(b))
      .slice(0, 8);
  })();

  const filteredInventory = inventory.filter(item => {
    if ((item.quantity || 0) <= 0) return false;

    let matchesSearch;
    if (searchQuery === '') {
      matchesSearch = true;
    } else if (queryIsExactName) {
      // Exact-name mode: show only this person's assets.
      matchesSearch = assigneeNames(item).includes(searchQuery);
    } else {
      // Partial mode: every word typed must appear somewhere in the item's searchable
      // text (including the assignee's FULL name, so "Pat Taylor" / "Patricia Taylor" match).
      const haystack = [
        item.description, item.item_type, item.type,
        item.manufacturer, item.brand, item.model,
        item.serial_number, item.asset_tag, item.hostname, item.mac_address,
        item.first_name, item.last_name,
        `${item.first_name || ''} ${item.last_name || ''}`,
        item.username, item.office_name, item.office_number,
        String(item.office_id || '')
      ].filter(Boolean).join(' ').toLowerCase();
      const tokens = searchQuery.split(/\s+/).filter(Boolean);
      matchesSearch = tokens.every(t => haystack.includes(t));
    }

    const matchesStaff = filterStaff === 'all' || item.staff_id === parseInt(filterStaff);

    // Category tab. Real data categories are mixed-case (e.g. "Computer", "Monitor",
    // "Phone"), so compare case-insensitively and treat "equipment" as any hardware
    // that is neither furniture nor software.
    const cat = String(item.category || '').toLowerCase();
    const matchesTab = activeTab === 'all' ||
      (activeTab === 'furniture' && cat === 'furniture') ||
      (activeTab === 'software' && cat === 'software') ||
      (activeTab === 'equipment' && cat !== 'furniture' && cat !== 'software');

    // Granular filters
    const eq = (field, val) =>
      val === '' || String(item[field] || '').toLowerCase() === val.toLowerCase();
    const contains = (field, val) =>
      val === '' || String(item[field] || '').toLowerCase().includes(val.toLowerCase());

    const matchesCategory = eq('category', filters.category);
    const matchesType = eq('item_type', filters.item_type);
    const matchesBrand = eq('brand', filters.brand);
    const matchesModel = eq('model', filters.model);
    const matchesStatus = eq('status', filters.status);
    const matchesCondition = eq('condition', filters.condition);

    let matchesFilterStaff = true;
    if (filters.staff_id === '__unassigned__') matchesFilterStaff = !item.staff_id;
    else if (filters.staff_id !== '') matchesFilterStaff = item.staff_id === Number(filters.staff_id);

    const matchesOffice =
      filters.office_id === '' || Number(item.office_id) === Number(filters.office_id);

    const matchesSerial = contains('serial_number', filters.serial_number);
    const matchesAsset = contains('asset_tag', filters.asset_tag);
    const matchesMac = contains('mac_address', filters.mac_address);

    return matchesSearch && matchesStaff && matchesTab &&
      matchesCategory && matchesType && matchesBrand && matchesModel &&
      matchesStatus && matchesCondition && matchesFilterStaff && matchesOffice &&
      matchesSerial && matchesAsset && matchesMac;
  });

  // ---- Export / Print (operate on current filteredInventory) ----
  const assignedName = (item) =>
    (item.first_name || item.last_name)
      ? `${item.first_name || ''} ${item.last_name || ''}`.trim()
      : '';
  const locationName = (item) => item.office_name || item.office_number || '';

  const exportColumns = [
    'Category', 'Item Type', 'Brand', 'Model', 'Serial #', 'Asset Tag',
    'Hostname', 'MAC', 'Condition', 'Status', 'Quantity', 'Assigned To', 'Location', 'Notes'
  ];

  const exportRowArray = (item) => [
    item.category || '',
    item.item_type || '',
    item.brand || '',
    item.model || '',
    item.serial_number || '',
    item.asset_tag || '',
    item.hostname || '',
    item.mac_address || '',
    item.condition || '',
    item.status || '',
    item.quantity != null ? item.quantity : '',
    assignedName(item),
    locationName(item),
    item.notes || ''
  ];

  const exportCSV = () => {
    const csvCell = (val) => {
      const s = String(val == null ? '' : val);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [exportColumns.join(',')];
    filteredInventory.forEach(item => {
      lines.push(exportRowArray(item).map(csvCell).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'CAAM_inventory.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportExcel = () => {
    const rows = filteredInventory.map(item => {
      const arr = exportRowArray(item);
      const obj = {};
      exportColumns.forEach((col, i) => { obj[col] = arr[i]; });
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory');
    XLSX.writeFile(wb, 'CAAM_inventory.xlsx');
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    const cols = ['Category', 'Type', 'Brand', 'Model', 'Serial', 'Asset Tag', 'Condition', 'Status', 'Qty', 'Assigned To', 'Location'];
    const body = filteredInventory.map(item => [
      item.category || '',
      item.item_type || '',
      item.brand || '',
      item.model || '',
      item.serial_number || '',
      item.asset_tag || '',
      item.condition || '',
      item.status || '',
      item.quantity != null ? item.quantity : '',
      assignedName(item),
      locationName(item)
    ]);
    doc.setFontSize(14);
    doc.text('CAAM — Inventory', 14, 15);
    doc.setFontSize(9);
    doc.text(`Generated ${new Date().toLocaleString()}  ·  ${filteredInventory.length} item${filteredInventory.length !== 1 ? 's' : ''}`, 14, 22);
    autoTable(doc, {
      head: [cols],
      body,
      startY: 28,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [26, 58, 92] }
    });
    const w = window.open(doc.output('bloburl'), '_blank');
    if (!w) doc.save('CAAM_inventory.pdf');
  };

  if (!isAdmin) {
    return (
      <div>
        {filteredInventory.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">No Inventory Items</div>
            <div className="empty-state-text">You don't have any inventory items assigned yet.</div>
          </div>
        ) : (
          <div className="card">
            <div className="card-body">
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Item Name</th>
                      <th>Type</th>
                      <th>Manufacturer/Brand</th>
                      <th>Model</th>
                      <th>Serial Number</th>
                      <th>Asset Tag</th>
                      <th>Quantity</th>
                      <th>Condition</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInventory.map((item) => (
                      <tr key={item.id}>
                        <td>{item.description || '-'}</td>
                        <td>{item.item_type || item.type || '-'}</td>
                        <td>{item.manufacturer || item.brand || '-'}</td>
                        <td>{item.model || '-'}</td>
                        <td>{item.serial_number || '-'}</td>
                        <td>{item.asset_tag || '-'}</td>
                        <td>{item.quantity || 1}</td>
                        <td>
                          <span className={`badge badge-${item.condition === 'Good' ? 'success' : item.condition === 'Fair' ? 'warning' : 'secondary'}`}>
                            {item.condition || 'N/A'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {toast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: toast.type === 'error' ? '#dc2626' : '#10b981',
          color: '#fff',
          padding: '12px 20px',
          borderRadius: '6px',
          fontSize: '13px',
          fontWeight: '500',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 10000,
          animation: 'slideIn 0.3s ease-out'
        }}>
          {toast.message}
        </div>
      )}
      
      <div className="stats-grid">
        <div className="stat-card">
          <svg className="stat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <line x1="9" y1="3" x2="9" y2="21"/>
          </svg>
          <div className="stat-content">
            <div className="stat-label">Total Items</div>
            <div className="stat-value">{filteredInventory.length}</div>
          </div>
        </div>
        <div className="stat-card">
          <svg className="stat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          </svg>
          <div className="stat-content">
            <div className="stat-label">Staff Members</div>
            <div className="stat-value">{stats.total_users || 0}</div>
          </div>
        </div>
        <div className="stat-card">
          <svg className="stat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
          </svg>
          <div className="stat-content">
            <div className="stat-label">Computers</div>
            <div className="stat-value">{stats.total_computers || 0}</div>
          </div>
        </div>
        <div className="stat-card">
          <svg className="stat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
          </svg>
          <div className="stat-content">
            <div className="stat-label">Total Inventory</div>
            <div className="stat-value">{stats.total_inventory_items || 0}</div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            {/* Segmented category tabs */}
            <div style={{ display: 'inline-flex', background: '#F3F4F6', border: `1px solid ${BORDER}`, borderRadius: '9px', padding: '3px' }}>
              {['all', 'furniture', 'equipment', 'software'].map(tab => {
                const active = activeTab === tab;
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      padding: '6px 14px', fontSize: '12.5px', fontWeight: active ? 700 : 500,
                      background: active ? '#fff' : 'transparent',
                      color: active ? NAVY : MUTED,
                      border: 'none', borderRadius: '7px', cursor: 'pointer',
                      textTransform: 'capitalize', transition: 'all .15s',
                      boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                    }}
                  >
                    {tab}
                  </button>
                );
              })}
            </div>
            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button onClick={handleAddClick} style={btnPrimary} title="Add a new inventory item">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add New
              </button>
              <button onClick={() => setShowImport(true)} style={btnGhost} title="Import items from CSV">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                Import
              </button>
              <div style={{ width: '1px', height: '22px', background: BORDER, margin: '0 2px' }} />
              <button onClick={exportCSV} style={btnGhost} title="Export current view to CSV">Export CSV</button>
              <button onClick={exportExcel} style={btnGhost} title="Export current view to Excel">Export Excel</button>
              <button onClick={exportPDF} style={btnGhost} title="Open printable PDF of the current view">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 9V2h12v7"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/>
                </svg>
                Print PDF
              </button>
            </div>
          </div>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div className="search-bar" style={{ flex: 1, position: 'relative' }}>
              <svg className="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                type="text"
                className="search-input"
                placeholder="Search name, type, brand, serial, asset tag, user, office..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setShowSuggest(true); }}
                onFocus={() => setShowSuggest(true)}
                onBlur={() => setTimeout(() => setShowSuggest(false), 120)}
                style={{ fontSize: '12px', paddingRight: '30px' }}
              />
              {showSuggest && nameSuggestions.length > 0 && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 50,
                  background: '#fff', border: `1px solid ${BORDER}`, borderRadius: '10px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden', maxHeight: '260px', overflowY: 'auto'
                }}>
                  {nameSuggestions.map(name => (
                    <button
                      key={name}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); setSearchTerm(name); setShowSuggest(false); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px', width: '100%', textAlign: 'left',
                        padding: '9px 12px', border: 'none', borderBottom: `1px solid #F3F4F6`,
                        background: '#fff', cursor: 'pointer', fontSize: '13px', color: NAVY
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#F7F8FA'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={MUTED} strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                      </svg>
                      {name}
                    </button>
                  ))}
                </div>
              )}
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  title="Clear search"
                  aria-label="Clear search"
                  style={{
                    position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                    width: '18px', height: '18px', padding: 0, border: 'none', borderRadius: '50%',
                    background: '#E5E7EB', color: '#4B5563', cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '12px', lineHeight: 1,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#D1D5DB'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#E5E7EB'; }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              )}
            </div>
            <select
              style={{ ...fieldStyle, width: '210px' }}
              value={filterStaff}
              onChange={(e) => setFilterStaff(e.target.value)}
            >
              <option value="all">All Staff Members</option>
              {staffList.map(staff => (
                <option key={staff.id} value={staff.id}>
                  {staff.first_name} {staff.last_name}
                </option>
              ))}
            </select>
            <button
              onClick={() => setShowFilters(v => !v)}
              style={showFilters ? btnPrimary : btnGhost}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
              </svg>
              Filters
            </button>
          </div>

          {showFilters && (
            <div style={{
              marginBottom: '16px', padding: '16px', background: '#FAFBFC',
              border: `1px solid ${BORDER}`, borderRadius: '10px'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: '10px'
              }}>
                <div>
                  <label style={fieldLabel}>Category</label>
                  <select style={fieldStyle}
                    value={filters.category} onChange={(e) => setFilter('category', e.target.value)}>
                    <option value="">All categories</option>
                    {distinctValues('category').map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label style={fieldLabel}>Status</label>
                  <select style={fieldStyle}
                    value={filters.status} onChange={(e) => setFilter('status', e.target.value)}>
                    <option value="">Any status</option>
                    {distinctValues('status').map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label style={fieldLabel}>Condition</label>
                  <select style={fieldStyle}
                    value={filters.condition} onChange={(e) => setFilter('condition', e.target.value)}>
                    <option value="">Any condition</option>
                    {distinctValues('condition').map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label style={fieldLabel}>Type</label>
                  <select style={fieldStyle}
                    value={filters.item_type} onChange={(e) => setFilter('item_type', e.target.value)}>
                    <option value="">All types</option>
                    {distinctValues('item_type').map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label style={fieldLabel}>Brand</label>
                  <select style={fieldStyle}
                    value={filters.brand} onChange={(e) => setFilter('brand', e.target.value)}>
                    <option value="">All brands</option>
                    {distinctValues('brand').map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label style={fieldLabel}>Model</label>
                  <select style={fieldStyle}
                    value={filters.model} onChange={(e) => setFilter('model', e.target.value)}>
                    <option value="">All models</option>
                    {distinctValues('model').map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label style={fieldLabel}>Assigned To</label>
                  <select style={fieldStyle}
                    value={filters.staff_id} onChange={(e) => setFilter('staff_id', e.target.value)}>
                    <option value="">Any</option>
                    <option value="__unassigned__">Unassigned</option>
                    {staffList.map(staff => (
                      <option key={staff.id} value={staff.id}>
                        {staff.first_name} {staff.last_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={fieldLabel}>Location</label>
                  <select style={fieldStyle}
                    value={filters.office_id} onChange={(e) => setFilter('office_id', e.target.value)}>
                    <option value="">Any location</option>
                    {distinctOffices().map(([id, label]) => (
                      <option key={id} value={id}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={fieldLabel}>Serial #</label>
                  <input type="text" placeholder="contains..."
                    value={filters.serial_number} onChange={(e) => setFilter('serial_number', e.target.value)}
                    style={fieldStyle} />
                </div>
                <div>
                  <label style={fieldLabel}>Asset Tag</label>
                  <input type="text" placeholder="contains..."
                    value={filters.asset_tag} onChange={(e) => setFilter('asset_tag', e.target.value)}
                    style={fieldStyle} />
                </div>
                <div>
                  <label style={fieldLabel}>MAC Address</label>
                  <input type="text" placeholder="contains..."
                    value={filters.mac_address} onChange={(e) => setFilter('mac_address', e.target.value)}
                    style={fieldStyle} />
                </div>
              </div>
              <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={clearFilters} style={btnGhost}>Clear filters</button>
              </div>
            </div>
          )}

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Staff Member</th>
                  <th>Category</th>
                  <th>Type</th>
                  <th>Brand</th>
                  <th>Model</th>
                  <th>Serial Number</th>
                  <th>Asset Tag</th>
                  <th>Qty</th>
                  <th style={{ textAlign: 'center', width: '110px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInventory.length === 0 ? (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '24px', color: '#a3a3a3' }}>
                      No inventory items found
                    </td>
                  </tr>
                ) : (
                  filteredInventory.map((item) => (
                    <tr key={item.id}>
                      <td>
                        {item.first_name && item.last_name ? (
                          <>
                            <div style={{ fontWeight: '500', fontSize: '12px' }}>
                              {item.first_name} {item.last_name}
                            </div>
                            <div style={{ fontSize: '10px', color: '#a3a3a3' }}>
                              {item.username}
                            </div>
                          </>
                        ) : (
                          <span style={{ color: '#10b981', fontSize: '11px', fontWeight: '600' }}>UNASSIGNED</span>
                        )}
                      </td>
                      <td>{item.category || '-'}</td>
                      <td>{item.item_type || item.type || '-'}</td>
                      <td>{item.manufacturer || item.brand || '-'}</td>
                      <td>{item.model || '-'}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                        {item.serial_number || '-'}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                        {item.asset_tag || '-'}
                      </td>
                      <td>{item.quantity || 1}</td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                          <button
                            onClick={() => handleEditClick(item)}
                            style={iconBtn}
                            title="Edit item"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EA6F2E" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                          {!item.staff_id && (
                            <button
                              onClick={() => handleAssignClick(item)}
                              style={iconBtn}
                              title="Assign to staff"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={NAVY} strokeWidth="2">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                <circle cx="8.5" cy="7" r="4"/>
                                <line x1="20" y1="8" x2="20" y2="14"/>
                                <line x1="23" y1="11" x2="17" y2="11"/>
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(item)}
                            style={iconBtn}
                            title="Delete item"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B42318" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                              <path d="M10 11v6M14 11v6"/>
                              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showEditModal && (selectedItem || isAdding) && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', width: '520px', maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: NAVY }}>{isAdding ? 'Add Inventory Item' : 'Edit Item'}</h3>
              <button onClick={() => { setShowEditModal(false); setIsAdding(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#737373' }}>×</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={fieldLabel}>Assigned To</label>
                <select
                  value={editForm.staff_id}
                  onChange={(e) => setEditForm({ ...editForm, staff_id: e.target.value })}
                  style={fieldStyle}
                >
                  <option value="">— Unassigned —</option>
                  {staffList.map(staff => (
                    <option key={staff.id} value={staff.id}>
                      {staff.first_name} {staff.last_name} ({staff.username})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={fieldLabel}>Category</label>
                <input
                  type="text"
                  value={editForm.category}
                  onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                  style={fieldStyle}
                />
              </div>
              <div>
                <label style={fieldLabel}>Type</label>
                <input
                  type="text"
                  value={editForm.item_type}
                  onChange={(e) => setEditForm({...editForm, item_type: e.target.value})}
                  style={fieldStyle}
                />
              </div>
              <div>
                <label style={fieldLabel}>Brand</label>
                <input
                  type="text"
                  value={editForm.brand}
                  onChange={(e) => setEditForm({...editForm, brand: e.target.value})}
                  style={fieldStyle}
                />
              </div>
              <div>
                <label style={fieldLabel}>Model</label>
                <input
                  type="text"
                  value={editForm.model}
                  onChange={(e) => setEditForm({...editForm, model: e.target.value})}
                  style={fieldStyle}
                />
              </div>
              <div>
                <label style={fieldLabel}>Serial Number</label>
                <input
                  type="text"
                  value={editForm.serial_number}
                  onChange={(e) => setEditForm({...editForm, serial_number: e.target.value})}
                  style={fieldStyle}
                />
              </div>
              <div>
                <label style={fieldLabel}>Asset Tag</label>
                <input
                  type="text"
                  value={editForm.asset_tag}
                  onChange={(e) => setEditForm({...editForm, asset_tag: e.target.value})}
                  style={fieldStyle}
                />
              </div>
              <div>
                <label style={fieldLabel}>Hostname</label>
                <input
                  type="text"
                  value={editForm.hostname}
                  onChange={(e) => setEditForm({...editForm, hostname: e.target.value})}
                  style={fieldStyle}
                />
              </div>
              <div>
                <label style={fieldLabel}>MAC Address</label>
                <input
                  type="text"
                  value={editForm.mac_address}
                  onChange={(e) => setEditForm({...editForm, mac_address: e.target.value})}
                  style={fieldStyle}
                />
              </div>
              <div>
                <label style={fieldLabel}>Condition</label>
                <input
                  type="text"
                  value={editForm.condition}
                  onChange={(e) => setEditForm({...editForm, condition: e.target.value})}
                  style={fieldStyle}
                  placeholder="e.g. Good, Fair, New"
                />
              </div>
              <div>
                <label style={fieldLabel}>Quantity</label>
                <input
                  type="number"
                  min="1"
                  value={editForm.quantity}
                  onChange={(e) => setEditForm({...editForm, quantity: e.target.value})}
                  style={fieldStyle}
                />
              </div>
              {!isAdding && (
                <div>
                  <label style={fieldLabel}>Status</label>
                  <input
                    type="text"
                    value={editForm.status}
                    onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                    style={fieldStyle}
                    placeholder="active / available"
                  />
                </div>
              )}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={fieldLabel}>Description</label>
                <input
                  type="text"
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  style={fieldStyle}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={fieldLabel}>Notes</label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  rows="3"
                  style={{ ...fieldStyle, resize: 'vertical' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', marginTop: '16px' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                {!isAdding && selectedItem?.staff_id && (
                  <button
                    onClick={handleReplaceClick}
                    disabled={saving}
                    style={{ ...btnGhost, opacity: saving ? 0.5 : 1 }}
                  >
                    Assign Different Item
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => { setShowEditModal(false); setIsAdding(false); }}
                  style={btnGhost}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={saving}
                  style={{ ...btnPrimary, opacity: saving ? 0.5 : 1 }}
                >
                  {saving ? (isAdding ? 'Adding...' : 'Saving...') : (isAdding ? 'Create Item' : 'Save Changes')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showReplaceModal && selectedItem && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }}>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', width: '480px', maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Assign Different {selectedItem.category}</h3>
              <button onClick={() => setShowReplaceModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#737373' }}>×</button>
            </div>

            <div style={{ marginBottom: '16px', padding: '12px', background: '#fff3cd', borderRadius: '6px', border: '1px solid #ffc107' }}>
              <div style={{ fontSize: '11px', color: '#856404' }}>
                This will return <strong>{selectedItem.brand} {selectedItem.model}</strong> to inventory and assign a new item to <strong>{selectedItem.first_name} {selectedItem.last_name}</strong>.
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={fieldLabel}>Select Replacement</label>
              <select
                value={selectedReplacement}
                onChange={(e) => setSelectedReplacement(e.target.value)}
                style={fieldStyle}
              >
                <option value="">Choose available item...</option>
                {availableItems.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.brand} {item.model} {item.item_type && `(${item.item_type})`} {item.serial_number && `- SN: ${item.serial_number}`}
                  </option>
                ))}
              </select>
            </div>

            {availableItems.length === 0 && (
              <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '6px', marginBottom: '16px' }}>
                <div style={{ fontSize: '11px', color: '#737373' }}>
                  No available {selectedItem.category} items in inventory.
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button 
                onClick={() => setShowReplaceModal(false)} 
                style={btnGhost}
              >
                Cancel
              </button>
              <button 
                onClick={handleReplace}
                disabled={!selectedReplacement || saving}
                style={{ ...btnPrimary, opacity: (!selectedReplacement || saving) ? 0.5 : 1 }}
              >
                {saving ? 'Replacing...' : 'Replace Item'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAssignModal && selectedItem && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', width: '420px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Assign Item to Staff</h3>
              <button onClick={() => setShowAssignModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#737373' }}>×</button>
            </div>
            
            <div style={{ marginBottom: '16px', padding: '12px', background: '#f9fafb', borderRadius: '6px' }}>
              <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>{selectedItem.description || selectedItem.item_type}</div>
              <div style={{ fontSize: '11px', color: '#737373' }}>
                {selectedItem.brand && `${selectedItem.brand} `}
                {selectedItem.model && `${selectedItem.model} `}
                {selectedItem.serial_number && `• SN: ${selectedItem.serial_number}`}
              </div>
              <div style={{ fontSize: '11px', color: '#737373', marginTop: '4px' }}>
                Available: {selectedItem.quantity || 0} unit{(selectedItem.quantity || 0) !== 1 ? 's' : ''}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={fieldLabel}>Assign To</label>
              <select
                value={selectedStaff}
                onChange={(e) => setSelectedStaff(e.target.value)}
                style={fieldStyle}
              >
                <option value="">Select Staff Member...</option>
                {staffList.map(staff => (
                  <option key={staff.id} value={staff.id}>
                    {staff.first_name} {staff.last_name} ({staff.username})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button 
                onClick={() => setShowAssignModal(false)} 
                style={btnGhost}
              >
                Cancel
              </button>
              <button 
                onClick={handleAssign}
                disabled={!selectedStaff || selectedStaff === 'new' || assigning}
                style={{ ...btnPrimary, opacity: (!selectedStaff || selectedStaff === 'new' || assigning) ? 0.5 : 1 }}
              >
                {assigning ? 'Assigning...' : 'Assign Item'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showImport && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', width: '480px', maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Import Inventory</h3>
              <button onClick={() => { setShowImport(false); setImportFile(null); setImportPreview([]); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#737373' }}>×</button>
            </div>
            <div style={{ marginBottom: '14px' }}>
              <label style={fieldLabel}>Category</label>
              <select value={importCategory} onChange={(e) => setImportCategory(e.target.value)} style={fieldStyle}>
                <option value="furniture">Furniture</option>
                <option value="equipment">Equipment</option>
                <option value="software">Software</option>
              </select>
            </div>
            <div style={{ marginBottom: '14px' }}>
              <button onClick={() => downloadTemplate(importCategory)} style={{ fontSize: '11px', color: '#EA6F2E', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '4px', verticalAlign: 'middle' }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Download {importCategory} template
              </button>
            </div>
            <div style={{ marginBottom: '14px' }}>
              <label style={fieldLabel}>CSV File</label>
              <input type="file" accept=".csv" onChange={handleFileSelect} style={{ fontSize: '12px' }} />
            </div>
            {importPreview.length > 0 && (
              <div style={{ marginBottom: '14px' }}>
                <label style={fieldLabel}>Preview (first 5 rows)</label>
                <div style={{ overflow: 'auto', maxHeight: '140px', border: '1px solid #e5e5e5', borderRadius: '4px' }}>
                  <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f5f5f5' }}>
                        {Object.keys(importPreview[0]).map(key => <th key={key} style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e5e5e5', fontWeight: '600' }}>{key}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.map((row, i) => (
                        <tr key={i}>
                          {Object.values(row).map((val, j) => <td key={j} style={{ padding: '6px 8px', borderBottom: '1px solid #e5e5e5' }}>{val}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button onClick={() => { setShowImport(false); setImportFile(null); setImportPreview([]); }} style={btnGhost}>Cancel</button>
              <button onClick={handleImport} disabled={!importFile || importing} style={{ ...btnPrimary, opacity: (!importFile || importing) ? 0.5 : 1 }}>{importing ? 'Importing...' : 'Import'}</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default InventoryManager;
